import { EventEmitter } from 'events';
import {
  DisconnectReason,
  Participant,
  ParticipantKind,
  RemoteAudioTrack,
  RemoteParticipant,
  RemoteVideoTrack,
  Room,
  RoomEvent,
  Track,
  createAudioAnalyser,
} from 'livekit-client';
import type {
  AudioAnalyserOptions,
  LocalAudioTrack,
  RemoteTrack,
  RemoteTrackPublication,
  TextStreamReader,
} from 'livekit-client';

import { SDK_VERSION } from '../core/generated/sdk-version.js';
import { AgentReadinessDetector } from '../core/logic/agent-readiness.js';
import { configureLivekitLogging } from '../core/logic/livekit-logging.js';
import { acquireMicrophone, observeMicPermissionGrant } from '../core/logic/mic-acquisition.js';
import type { MicStatus } from '../core/logic/mic-availability.js';
import { MuteController, type MuteControllerState } from '../core/logic/mute-controller.js';
import {
  TRANSCRIPTION_TOPIC,
  consumeTranscriptionStream,
} from '../core/logic/transcription-stream.js';
import { ServiceMode, parseServiceMode } from '../core/types/service-mode.js';

import { DealerAudio, type AudioAnalyser } from './audio.js';
import { fetchSession } from './connection.js';
import {
  AlreadyConnectedError,
  type AgentState,
  type AvatarStatus,
  type DealerVideoTrack,
  type SDKEventMap,
  type SDKEventName,
  type SDKLogger,
  type SDKServiceConfig,
  type SDKStateSnapshot,
  type ToolEffect,
  type TranscriptionSpeaker,
} from './types.js';

const AGENT_STATE_ATTR = 'lk.agent.state';
const PUBLISH_ON_BEHALF_ATTR = 'lk.publish_on_behalf';
// Read + logged by the agent (voice_agent.py) for fleet visibility (ADR 0037).
const SDK_VERSION_ATTR = 'aq.sdk_version';
const PTT_RPC_TIMEOUT_MS = 5000;
const GAME_EVENT_RPC_TIMEOUT_MS = 3000;
const MUTE_RPC_TIMEOUT_MS = 3000;

type ConnectionPhase = 'idle' | 'connecting' | 'connected' | 'disconnecting';

const NOOP_LOGGER: SDKLogger = {
  debug: () => {},
  info: () => {},
  warn: () => {},
  error: () => {},
};

export interface DealerSDK {
  on<K extends SDKEventName>(event: K, listener: (payload: SDKEventMap[K]) => void): this;
  off<K extends SDKEventName>(event: K, listener: (payload: SDKEventMap[K]) => void): this;
  once<K extends SDKEventName>(event: K, listener: (payload: SDKEventMap[K]) => void): this;
  emit<K extends SDKEventName>(event: K, payload: SDKEventMap[K]): boolean;
  removeAllListeners<K extends SDKEventName>(event?: K): this;
}

/**
 * Transport class — thin wrapper over `livekit-client` + SDK event surface.
 * Service wrappers (BlackjackSDKService / RouletteSDKService) compose this
 * and add game-specific validation and method shape. Studios with their own
 * game semantics can use `DealerSDK` directly for raw access.
 */
export class DealerSDK extends EventEmitter {
  private readonly config: SDKServiceConfig;
  private readonly logger: SDKLogger;
  private readonly audioContainer: HTMLElement;

  private phase: ConnectionPhase = 'idle';
  // FIFO chain for game-event RPCs — see sendGameEvent for why order matters.
  private gameEventSendChain: Promise<unknown> = Promise.resolve();
  private room: Room | null = null;
  private agent: RemoteParticipant | null = null;
  // Avatar worker: a kind=AGENT participant whose `lk.publish_on_behalf` matches
  // `this.agent.identity`. Publishes the audio + video tracks the user hears
  // and sees when an avatar plugin (Simli, Tavus, Bey, Hedra, …) is enabled
  // on the Python agent. RPCs and `lk.agent.state` always target the primary.
  private avatarWorker: RemoteParticipant | null = null;
  // 'disconnected' = agent absent (never joined or left); truthful for widget
  // renderers that disable PTT until the agent is present.
  private agentState: AgentState = 'disconnected';
  private serviceMode: ServiceMode = ServiceMode.Normal;
  private videoTrack: DealerVideoTrack | null = null;
  private avatarStatus: AvatarStatus | null = null;
  // Autoplay permission for the dealer's TTS audio, updated from
  // RoomEvent.AudioPlaybackStatusChanged. Optimistic default until told otherwise.
  private canPlaybackAudio = true;
  // Mic availability from the latest PTT acquisition attempt. Optimistic 'ok'
  // until a hold proves otherwise. Not reset on disconnect — the browser
  // permission outlives the room.
  private micStatus: MicStatus = 'ok';
  private stopMicPermissionWatch: (() => void) | null = null;
  // Local-mic analyser cache: createAudioAnalyser opens an AudioContext, so one
  // instance is reused until the underlying track changes (mic re-acquired).
  private localAnalyser: AudioAnalyser | null = null;
  private localAnalyserTrack: LocalAudioTrack | null = null;
  private localAnalyserMediaTrack: MediaStreamTrack | null = null;
  // Deliberate: not reset on disconnect so a same-identity agent reconnect
  // won't re-emit `ready` (matches AgentReadinessDetector semantics — ready
  // re-arms only on identity change).
  private readyEmittedForIdentity: string | null = null;

  private readonly audio: DealerAudio;
  private readonly muteController: MuteController;
  private readonly readiness: AgentReadinessDetector;

  constructor(config: SDKServiceConfig) {
    super();
    // Quiet livekit-client's verbose INFO logging (room-join token in the
    // signal URL, connection/participant details) unless ?log=debug is set.
    configureLivekitLogging();
    this.setMaxListeners(50);
    this.config = config;
    this.logger = config.logger ?? NOOP_LOGGER;

    const container =
      config.audioContainer ?? (typeof document !== 'undefined' ? document.body : null);
    if (!container) {
      throw new Error(
        'DealerSDK requires a DOM environment or an explicit audioContainer in config'
      );
    }
    this.audioContainer = container;
    this.audio = new DealerAudio(this.audioContainer, config.initialMuted ?? false);

    this.muteController = new MuteController({
      initialMuted: config.initialMuted ?? false,
      sendRpc: (muted) => this.performMuteRpc(muted),
      onChange: (state) => this.handleMuteStateChange(state),
    });

    this.readiness = new AgentReadinessDetector({
      onReady: (state) => this.emitTyped('ready', state as AgentState),
    });
  }

  isReady(): boolean {
    return this.phase === 'connected' && this.readyEmittedForIdentity !== null;
  }

  getState(): SDKStateSnapshot {
    return {
      connected: this.phase === 'connected',
      agentState: this.agentState,
      serviceMode: this.serviceMode,
      muted: this.muteController.getState().muted,
      audioAttached: this.audio.isAttached(),
      canPlaybackAudio: this.room?.canPlaybackAudio ?? this.canPlaybackAudio,
      micStatus: this.micStatus,
      videoTrack: this.videoTrack,
      avatarStatus: this.avatarStatus,
    };
  }

  getVideoTrack(): DealerVideoTrack | null {
    return this.videoTrack;
  }

  getAudioAnalyser(options?: Parameters<DealerAudio['getAnalyser']>[0]): AudioAnalyser | null {
    return this.audio.getAnalyser(options);
  }

  /**
   * Analyser over the local microphone track (for the holding-state visualizer).
   * `null` until the mic has been acquired by a PTT hold. The instance is cached;
   * a new one is created transparently if the mic track is re-acquired.
   */
  getLocalAudioAnalyser(options?: AudioAnalyserOptions): AudioAnalyser | null {
    const localParticipant = this.room?.localParticipant;
    const track = localParticipant?.getTrackPublication(Track.Source.Microphone)?.track;
    if (!track || track.kind !== Track.Kind.Audio) {
      return null;
    }
    const audioTrack = track as LocalAudioTrack;
    // The cache's real identity is the underlying MediaStreamTrack — that is
    // what the analyser consumes. A restart (ours after a dead-on-arrival
    // grant, or LiveKit's own unmute-reacquire) swaps it INSIDE the same
    // LocalAudioTrack instance, so keying on the instance alone kept the
    // analyser wired to the dead track: flat bars after a successful heal.
    if (
      !this.localAnalyser ||
      this.localAnalyserTrack !== audioTrack ||
      this.localAnalyserMediaTrack !== audioTrack.mediaStreamTrack
    ) {
      void this.localAnalyser?.cleanup();
      this.localAnalyser = createAudioAnalyser(audioTrack, options);
      this.localAnalyserTrack = audioTrack;
      this.localAnalyserMediaTrack = audioTrack.mediaStreamTrack;
    }
    return this.localAnalyser;
  }

  async connect(): Promise<void> {
    if (this.phase !== 'idle') {
      throw new AlreadyConnectedError();
    }
    this.phase = 'connecting';
    try {
      const session = await fetchSession(this.config.serviceUrl, this.config.sessionToken);

      // Identity line for support: a player's console screenshot resolves to a
      // session in the inspector (open-by-id). Logged before room.connect so a
      // failed connect is still attributable. Debug level — visible on dev by
      // default, on prod only when AQ_LOG_LEVEL is raised. Never the token.
      this.logger.debug(
        `session ${session.sessionId || 'unknown'} room ${session.roomName || 'unknown'}`
      );

      this.serviceMode = session.serviceMode;
      this.emitTyped('serviceMode', session.serviceMode);

      // Room defaults on purpose. `stopMicTrackOnMute` releases capture on every
      // mute, which on iOS costs a system beep and a permission banner on EVERY
      // press and buys nothing — the page stays in play-and-record while ANY mic
      // source lives, and the track is only one of them. So the track is merely
      // muted here, and the rule that actually matters is enforced elsewhere:
      // release every mic source between turns, the analyser's AudioContext
      // included (see releaseMicAudioGraph, #476 / ADR 0057 rule 2).
      const room = this.config.roomFactory ? this.config.roomFactory() : new Room();
      this.room = room;
      this.bindRoomEvents(room);

      await room.connect(session.livekitUrl, session.livekitToken);
      this.phase = 'connected';

      // Best-effort, off the connect path: tokens minted by an older api-server
      // lack canUpdateOwnMetadata and the server rejects the update — version
      // visibility must never fail or delay a connect.
      void room.localParticipant
        .setAttributes({ [SDK_VERSION_ATTR]: SDK_VERSION })
        .catch((err: unknown) => {
          this.logger.warn('sdk_version attribute publish failed (older server token?)', {
            error: err instanceof Error ? err.message : String(err),
          });
        });

      // Self-heal for a 'blocked' mic: permission granted in browser settings
      // flips micStatus back to 'ok' without waiting for another hold.
      this.stopMicPermissionWatch = observeMicPermissionGrant(() => this.updateMicStatus('ok'));

      this.seedAgentFromExistingParticipants(room);

      // Server-flag reconciliation stands down while the controller holds an
      // unconfirmed desire (a toggle whose RPC died with the transport, or one
      // queued pre-ready): the agent stamps the flag only on a SUCCESSFUL
      // set_mute, so it is stale here and would reverse the player's action —
      // the seed path above already resyncs the desired value (#482 review).
      if (
        !this.muteController.hasPendingDesire() &&
        session.dealerMuted !== this.muteController.getState().muted
      ) {
        void this.muteController.setMute(session.dealerMuted);
      }

      this.logger.info('DealerSDK connected', {
        roomName: session.roomName,
        serviceMode: session.serviceMode,
      });
    } catch (err) {
      this.phase = 'idle';
      const room = this.room;
      this.room = null;
      if (room) {
        // Setup failure: unbind FIRST so the awaited disconnect's
        // RoomEvent.Disconnected does NOT emit a phantom 'disconnected'
        // (error policy: setup failures reject the Promise only).
        this.unbindRoomEvents(room);
        try {
          await room.disconnect();
        } catch {
          // swallow; original error is what matters
        }
      }
      throw err;
    }
  }

  async disconnect(): Promise<void> {
    if (this.phase !== 'connected' && this.phase !== 'connecting') return;
    const room = this.room;
    this.phase = 'disconnecting';
    if (room) {
      await room.disconnect();
    }
  }

  destroy(): void {
    const room = this.room;
    if (room) {
      // Caller-initiated teardown is silent: unbind FIRST so the async
      // RoomEvent.Disconnected from disconnect() never reaches our handler
      // and no 'disconnected' is emitted (see types.ts SDKEventMap contract).
      this.unbindRoomEvents(room);
      void room.disconnect();
    }
    this.phase = 'idle';
    this.room = null;
    this.agent = null;
    this.avatarWorker = null;
    this.audio.detach();
    this.videoTrack?.detach();
    this.videoTrack = null;
    this.releaseLocalAnalyser();
    this.stopMicPermissionWatch?.();
    this.stopMicPermissionWatch = null;
    this.muteController.destroy();
    this.readiness.reset();
  }

  async startAudio(): Promise<void> {
    if (!this.room) {
      throw new Error('DealerSDK is not connected; call connect() first');
    }
    try {
      await this.room.startAudio();
    } finally {
      // livekit's startAudio() sets `element.muted = false` on every attached
      // element BEFORE it attempts play() (Room.ts, 2.22.0) — which is the
      // property the player's mute now rides (#473). A REJECTED call has still
      // un-muted by then, so the re-assert has to run either way: hence
      // `finally`, not a line after the await.
      this.audio.setMuted(this.muteController.getState().muted);
    }
  }

  async setDealerMute(muted: boolean): Promise<void> {
    await this.muteController.setMute(muted);
  }

  setVolume(volume: number): void {
    this.audio.setVolume(volume);
  }

  /**
   * Make the microphone ready. The browser's permission prompt rides the
   * caller's gesture, so the FIRST call can suspend for seconds — by which
   * time the gesture is usually over. Opens no turn: the caller re-checks that
   * the press is still live and then calls `openTurn` (#475).
   */
  async acquireMic(): Promise<void> {
    this.requireAgent(); // fail fast before touching the mic
    // Timed because the mic is released between turns (#476): every press pays
    // a re-arm, and that cost is the trade-off being measured on device. The
    // first call includes the permission prompt, so it is not comparable.
    const startedAt = Date.now();
    const status = await acquireMicrophone(this.requireLocalParticipant());
    this.logger.debug('mic acquire', { ms: Date.now() - startedAt, status });
    this.updateMicStatus(status);
    if (status !== 'ok') {
      throw new Error(`Microphone unavailable (${status})`);
    }
  }

  /**
   * Hand the microphone back. Capture is held only while a turn is open, so
   * an acquisition that did not become a turn releases it again.
   */
  async releaseMic(): Promise<void> {
    if (!this.room?.localParticipant) return; // nothing acquired to release
    await this.room.localParticipant.setMicrophoneEnabled(false);
    this.releaseMicAudioGraph();
  }

  /**
   * Drop everything that keeps a microphone source alive between turns. The
   * analyser opens its own AudioContext over the mic track, so leaving it
   * cached (we used to, until disconnect) pins the page in play-and-record
   * exactly as a live track would — which is why the dealer stayed dull and the
   * host game's effects died even when the track itself was released.
   * livekit's own visualiser hooks tie the analyser's life to the track; this
   * does the same.
   */
  private releaseMicAudioGraph(): void {
    this.releaseLocalAnalyser();
  }

  /** Open the turn at the agent. The caller guarantees the press is still live. */
  async openTurn(): Promise<void> {
    await this.performPTT('start_turn', true);
  }

  /**
   * Acquire the mic and open a turn in one call. Published SDK surface
   * (ADR 0036); hosts driving their own PTT button should prefer
   * `acquireMic` + `openTurn` so a press released during the permission
   * prompt does not open a turn the player never asked for (#475).
   */
  async startPTT(): Promise<void> {
    await this.acquireMic();
    await this.openTurn();
  }

  async endPTT(): Promise<void> {
    await this.performPTT('end_turn', false);
  }

  async cancelPTT(): Promise<void> {
    await this.performPTT('cancel_turn', false);
  }

  /**
   * Send a typed player message on LiveKit's standard chat text stream; the
   * agent treats it as a spoken turn (ADR 0065). Additive wire: no RPC.
   */
  async sendText(text: string): Promise<void> {
    const agent = this.requireAgent();
    // To the dealer only: the player's words never reach an avatar worker.
    await this.requireLocalParticipant().sendText(text, {
      topic: 'lk.chat',
      destinationIdentities: [agent.identity],
    });
  }

  async sendGameEvent(type: string, data?: unknown): Promise<{ success: boolean; error?: string }> {
    // Announce the game's intent before any transport checks: state-layer
    // consumers (suspension tracking) must see the event even if the agent is
    // briefly absent and the RPC below fails.
    this.emitTyped('gameEventSent', data === undefined ? { type } : { type, data });
    // Serialize sends: concurrent performRpc calls give no emission-order
    // guarantee, and back-to-back sends (e.g. the restore replay's
    // bet_confirmation + hands snapshot) were observed arriving inverted at
    // the agent. Each RPC starts only after the previous one settled; a
    // failed send never blocks the chain.
    const send = this.gameEventSendChain.then(() => this.performGameEventRpc(type, data));
    this.gameEventSendChain = send.then(
      () => undefined,
      () => undefined
    );
    return send;
  }

  private async performGameEventRpc(
    type: string,
    data?: unknown
  ): Promise<{ success: boolean; error?: string }> {
    const agent = this.requireAgent();
    const localParticipant = this.requireLocalParticipant();

    const payload = JSON.stringify({ type, data });

    try {
      const response = await localParticipant.performRpc({
        destinationIdentity: agent.identity,
        method: 'handle_game_event',
        payload,
        responseTimeout: GAME_EVENT_RPC_TIMEOUT_MS,
      });
      const parsed = JSON.parse(response) as { success?: unknown; error?: unknown };
      // Business failures (agent said "can't process") return via the
      // {success: false, error} contract only — not an 'error' event.
      // `error` is reserved for RPC/network exceptions (the catch below).
      if (parsed.success !== true) {
        const message =
          typeof parsed.error === 'string'
            ? parsed.error
            : `Game event '${type}' processing failed`;
        return { success: false, error: message };
      }
      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.emitError('GAME_EVENT_RPC_ERROR', message, err);
      throw err instanceof Error ? err : new Error(message);
    }
  }

  // --- Internals ---

  private bindRoomEvents(room: Room): void {
    // Transcriptions arrive as text streams (the non-deprecated delivery path);
    // RoomEvent.TranscriptionReceived is legacy and never fires for stream writes.
    room.registerTextStreamHandler(TRANSCRIPTION_TOPIC, this.handleTranscriptionStream);
    room.on(RoomEvent.Disconnected, this.handleRoomDisconnected);
    room.on(RoomEvent.ParticipantConnected, this.handleParticipantConnected);
    room.on(RoomEvent.ParticipantDisconnected, this.handleParticipantDisconnected);
    room.on(RoomEvent.ParticipantAttributesChanged, this.handleAttributesChanged);
    room.on(RoomEvent.TrackSubscribed, this.handleTrackSubscribed);
    room.on(RoomEvent.TrackUnsubscribed, this.handleTrackUnsubscribed);
    room.on(RoomEvent.DataReceived, this.handleDataReceived);
    room.on(RoomEvent.Reconnecting, this.handleReconnecting);
    room.on(RoomEvent.Reconnected, this.handleReconnected);
    room.on(RoomEvent.AudioPlaybackStatusChanged, this.handleAudioPlaybackChanged);
  }

  private unbindRoomEvents(room: Room): void {
    room.unregisterTextStreamHandler(TRANSCRIPTION_TOPIC);
    room.off(RoomEvent.Disconnected, this.handleRoomDisconnected);
    room.off(RoomEvent.ParticipantConnected, this.handleParticipantConnected);
    room.off(RoomEvent.ParticipantDisconnected, this.handleParticipantDisconnected);
    room.off(RoomEvent.ParticipantAttributesChanged, this.handleAttributesChanged);
    room.off(RoomEvent.TrackSubscribed, this.handleTrackSubscribed);
    room.off(RoomEvent.TrackUnsubscribed, this.handleTrackUnsubscribed);
    room.off(RoomEvent.DataReceived, this.handleDataReceived);
    room.off(RoomEvent.Reconnecting, this.handleReconnecting);
    room.off(RoomEvent.Reconnected, this.handleReconnected);
    room.off(RoomEvent.AudioPlaybackStatusChanged, this.handleAudioPlaybackChanged);
  }

  private seedAgentFromExistingParticipants(room: Room): void {
    // Two passes: identify the primary first, then bind the avatar worker
    // (if any) using the primary's identity.
    for (const participant of room.remoteParticipants.values()) {
      if (this.isPrimaryAgent(participant)) {
        this.handleParticipantConnected(participant);
        break;
      }
    }
    if (!this.agent) return;
    for (const participant of room.remoteParticipants.values()) {
      if (this.isAvatarWorkerFor(participant, this.agent.identity)) {
        this.handleParticipantConnected(participant);
        break;
      }
    }
  }

  private handleRoomDisconnected = (reason?: DisconnectReason): void => {
    this.logger.info('DealerSDK room disconnected', { reason });
    const room = this.room;
    if (room) this.unbindRoomEvents(room); // self-unbind: the single transport-teardown point
    this.audio.detach();
    this.videoTrack?.detach();
    this.videoTrack = null;
    this.releaseLocalAnalyser();
    this.stopMicPermissionWatch?.();
    this.stopMicPermissionWatch = null;
    this.avatarStatus = null;
    this.canPlaybackAudio = true;
    this.agent = null;
    this.avatarWorker = null;
    this.agentState = 'disconnected';
    this.emitTyped('agentState', 'disconnected');
    this.emitTyped('speaking', false);
    this.muteController.setReady(false);
    this.phase = 'idle';
    this.room = null;
    // CLIENT_INITIATED can only originate on our own side. destroy() unbinds
    // before disconnecting, so reaching here still bound means livekit's own
    // pagehide teardown (reload closes the socket before destroy() runs) or a
    // caller using disconnect() directly. Either way it is not a transport
    // failure: clean up, but emit no 'disconnected' (the contract reserves it
    // for failures the host should react to).
    if (reason !== DisconnectReason.CLIENT_INITIATED) {
      this.emitTyped(
        'disconnected',
        reason !== undefined ? { reason: DisconnectReason[reason] ?? String(reason) } : {}
      );
    }
  };

  private handleParticipantConnected = (participant: RemoteParticipant): void => {
    this.logger.debug('participant connected', {
      identity: participant.identity,
      sid: participant.sid,
      kind: participant.kind,
    });
    if (this.isPrimaryAgent(participant)) {
      this.agent = participant;
      this.muteController.setReady(true);
      // Presence itself is a state transition ('disconnected' → at least
      // 'idle'), even when the agent hasn't published its state attribute yet.
      const state = this.readAgentState(participant);
      this.applyAgentState(state ?? 'idle', participant.identity);
      return;
    }
    if (this.agent && this.isAvatarWorkerFor(participant, this.agent.identity)) {
      this.avatarWorker = participant;
      // Tracks (audio + video) arrive via TrackSubscribed; nothing more to do here.
      return;
    }
    // Reached for AGENT-kind participants we couldn't classify as primary or
    // current-primary's worker. Two known shapes:
    //   1. Worker arrived before primary (out-of-order; LiveKit's dispatch
    //      order makes this rare but legal).
    //   2. Worker still alive in room with stale `lk.publish_on_behalf` after
    //      a primary identity change (mid-session reconnect with new identity).
    // Both are out of scope for the current SDK; logging surfaces them so a
    // future debugger can identify the case without re-instrumenting.
    if (participant.kind === ParticipantKind.AGENT) {
      this.logger.debug('DealerSDK: unrecognized AGENT participant', {
        identity: participant.identity,
        attributes: participant.attributes,
        currentPrimary: this.agent?.identity,
      });
    }
  };

  private handleParticipantDisconnected = (participant: RemoteParticipant): void => {
    if (this.agent && participant.identity === this.agent.identity) {
      this.agent = null;
      // Worker is bound to its primary; without a primary, the worker reference
      // is stale even if the worker participant is still in the room.
      this.avatarWorker = null;
      this.agentState = 'disconnected';
      this.emitTyped('agentState', 'disconnected');
      this.emitTyped('speaking', false);
      this.muteController.setReady(false);
      return;
    }
    if (this.avatarWorker && participant.identity === this.avatarWorker.identity) {
      this.avatarWorker = null;
      // Primary is still serving — don't change agentState or mute readiness.
    }
  };

  private handleAttributesChanged = (
    _changed: Record<string, string>,
    participant: Participant
  ): void => {
    // Only the primary owns `lk.agent.state`. Worker attribute changes (if any)
    // are not part of the SDK's surface today.
    if (!this.isPrimaryAgent(participant as RemoteParticipant)) return;
    if (!this.agent) {
      this.agent = participant as RemoteParticipant;
      this.muteController.setReady(true);
    }
    const state = this.readAgentState(participant);
    if (state) {
      this.applyAgentState(state, participant.identity);
    }
  };

  private handleTrackSubscribed = (
    track: RemoteTrack,
    _publication: RemoteTrackPublication,
    participant: RemoteParticipant
  ): void => {
    // Accept tracks from primary OR avatar worker — audio + video may come
    // from either depending on whether an avatar plugin is enabled on the agent.
    if (!this.isAgentParticipant(participant)) return;
    if (track.kind === Track.Kind.Audio) {
      this.audio.attach(track as RemoteAudioTrack);
    } else if (track.kind === Track.Kind.Video) {
      this.videoTrack = this.wrapVideoTrack(track as RemoteVideoTrack);
      this.emitTyped('videoTrack', this.videoTrack);
    }
  };

  private handleTrackUnsubscribed = (
    track: RemoteTrack,
    _publication: RemoteTrackPublication,
    participant: RemoteParticipant
  ): void => {
    if (!this.isAgentParticipant(participant)) return;
    if (track.kind === Track.Kind.Audio) {
      this.audio.detach();
    } else if (track.kind === Track.Kind.Video) {
      this.videoTrack?.detach();
      this.videoTrack = null;
    }
  };

  private handleDataReceived = (payload: Uint8Array): void => {
    try {
      const text = new TextDecoder().decode(payload);
      const packet = JSON.parse(text) as Record<string, unknown>;
      const type = typeof packet.type === 'string' ? packet.type : undefined;
      if (type === 'service_mode') {
        const mode = parseServiceMode(packet.mode);
        this.serviceMode = mode;
        this.emitTyped('serviceMode', mode);
      } else if (type === 'tool_effect') {
        const effect = typeof packet.effect === 'string' ? packet.effect : 'unknown';
        const data =
          typeof packet.data === 'object' && packet.data !== null
            ? (packet.data as Record<string, unknown>)
            : {};
        const toolEffect: ToolEffect = { effect, data };
        this.emitTyped('toolEffect', toolEffect);
      } else if (type === 'avatar_status') {
        const status: AvatarStatus | undefined =
          packet.status === 'active' || packet.status === 'unavailable' ? packet.status : undefined;
        if (status) {
          this.avatarStatus = status;
          this.emitTyped('avatarStatus', { status });
        }
      } else {
        this.logger.debug('DealerSDK: unknown data packet', packet);
      }
    } catch (err) {
      this.logger.warn('DealerSDK: failed to parse data packet', {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  };

  private handleTranscriptionStream = (
    reader: TextStreamReader,
    participantInfo: { identity: string }
  ): void => {
    void this.consumeTranscriptionStream(reader, participantInfo.identity);
  };

  private async consumeTranscriptionStream(
    reader: TextStreamReader,
    senderIdentity: string
  ): Promise<void> {
    const speaker = this.resolveTranscriptionSpeaker(senderIdentity);
    if (!speaker) {
      this.logger.debug('DealerSDK: transcription from unrecognized sender', { senderIdentity });
      return;
    }
    try {
      await consumeTranscriptionStream(reader, ({ segmentId, text, final }) => {
        this.emitTyped('transcription', { segmentId, text, final, speaker });
      });
    } catch (err) {
      this.logger.warn('DealerSDK: transcription stream aborted', {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  /**
   * Attribute captions by SENDER identity: the agents runtime publishes
   * transcription text streams from the primary agent even when an avatar
   * worker owns the audio track (`lk.transcribed_track_id` is omitted there),
   * so identity is the only correlation that works with and without an avatar.
   */
  private resolveTranscriptionSpeaker(senderIdentity: string): TranscriptionSpeaker | null {
    if (senderIdentity === this.room?.localParticipant?.identity) {
      return 'player';
    }
    if (this.agent && senderIdentity === this.agent.identity) {
      return 'dealer';
    }
    // Stream may arrive before the primary is classified (rare join-order race).
    const participant = this.room?.remoteParticipants.get(senderIdentity);
    if (participant && this.isPrimaryAgent(participant)) {
      return 'dealer';
    }
    return null;
  }

  private updateMicStatus(status: MicStatus): void {
    if (status === this.micStatus) return;
    this.micStatus = status;
    this.emitTyped('micStatus', { status });
  }

  private releaseLocalAnalyser(): void {
    void this.localAnalyser?.cleanup();
    this.localAnalyser = null;
    this.localAnalyserTrack = null;
    this.localAnalyserMediaTrack = null;
  }

  private handleReconnecting = (): void => {
    this.logger.info('DealerSDK reconnecting');
    this.emitTyped('connectionState', { state: 'reconnecting' });
  };

  private handleReconnected = (): void => {
    this.logger.info('DealerSDK reconnected');
    this.emitTyped('connectionState', { state: 'reconnected' });
    // A mute RPC that raced the reconnect timed out; the agent never got it (#478).
    void this.muteController.resync();
  };

  private handleAudioPlaybackChanged = (): void => {
    // LiveKit fires this with no args; read the authoritative getter.
    const can = this.room?.canPlaybackAudio ?? true;
    if (can === this.canPlaybackAudio) return; // emit only on real change
    this.canPlaybackAudio = can;
    this.logger.info('DealerSDK audio playback status changed', { canPlayback: can });
    this.emitTyped('audioPlaybackChanged', { canPlayback: can });
  };

  private async performPTT(
    method: 'start_turn' | 'end_turn' | 'cancel_turn',
    micEnabled: boolean
  ): Promise<void> {
    const agent = this.requireAgent();
    const localParticipant = this.requireLocalParticipant();
    try {
      const startedAt = Date.now();
      await localParticipant.performRpc({
        destinationIdentity: agent.identity,
        method,
        payload: '',
        responseTimeout: PTT_RPC_TIMEOUT_MS,
      });
      this.logger.debug('ptt rpc ok', {
        method,
        ms: Date.now() - startedAt,
        agent: agent.identity,
      });
      await localParticipant.setMicrophoneEnabled(micEnabled);
      if (!micEnabled) this.releaseMicAudioGraph();
    } catch (err) {
      try {
        await localParticipant.setMicrophoneEnabled(false);
        this.releaseMicAudioGraph();
      } catch {
        // ignore
      }
      const message = err instanceof Error ? err.message : String(err);
      this.emitError(`PTT_${method.toUpperCase()}_FAILED`, message, err);
      throw err instanceof Error ? err : new Error(message);
    }
  }

  private async performMuteRpc(muted: boolean): Promise<{ muted: boolean } | null> {
    const agent = this.requireAgent();
    const localParticipant = this.requireLocalParticipant();
    this.logger.debug('mute rpc send', { muted, agent: agent.identity });
    try {
      const response = await localParticipant.performRpc({
        destinationIdentity: agent.identity,
        method: 'set_mute',
        payload: JSON.stringify({ muted }),
        responseTimeout: MUTE_RPC_TIMEOUT_MS,
      });
      const parsed = JSON.parse(response ?? '{}') as { muted?: unknown };
      this.logger.debug('mute rpc ok', { muted, response: parsed.muted });
      return { muted: typeof parsed.muted === 'boolean' ? parsed.muted : muted };
    } catch (err) {
      this.logger.debug('mute rpc failed', {
        muted,
        error: err instanceof Error ? err.message : String(err),
      });
      throw err;
    }
  }

  private handleMuteStateChange(state: MuteControllerState): void {
    this.audio.setMuted(state.muted);
    this.emitTyped('mute', { muted: state.muted });
    if (state.error) {
      this.emitError('MUTE_RPC_ERROR', state.error);
    }
  }

  private applyAgentState(state: AgentState, identity: string): void {
    if (state !== this.agentState) {
      this.agentState = state;
      this.emitTyped('agentState', state);
      this.emitTyped('speaking', state === 'speaking');
    }
    this.readiness.observe(state, identity);
    if (state === 'listening' || state === 'thinking' || state === 'speaking') {
      this.readyEmittedForIdentity = identity;
    }
  }

  private readAgentState(participant: Participant): AgentState | null {
    const raw = participant.attributes?.[AGENT_STATE_ATTR];
    if (raw === 'idle' || raw === 'listening' || raw === 'thinking' || raw === 'speaking') {
      return raw;
    }
    return null;
  }

  /**
   * The primary agent or an avatar worker — gates track subscription. Audio +
   * video can come from either: pre-avatar setups publish from the primary,
   * avatar-enabled setups publish from a worker per LiveKit's avatar contract.
   *
   * Detection order:
   *   1. `kind === ParticipantKind.AGENT` — canonical signal published by the
   *      LiveKit server for any agent participant (primary or worker). This
   *      should match in all current setups.
   *   2. `agentIdentityPrefix` config — explicit consumer override for unusual
   *      deployments where the canonical kind isn't set as expected.
   *   3. `lk.agent.state` attribute presence — fallback covering legacy Python
   *      agents from before LiveKit standardised on ParticipantKind.AGENT
   *      (still in some long-lived deployments). Will become removable once
   *      production fully runs on agents that publish `kind=AGENT`.
   */
  private isAgentParticipant(participant: RemoteParticipant | Participant): boolean {
    if (participant.kind === ParticipantKind.AGENT) return true;
    if (
      this.config.agentIdentityPrefix &&
      participant.identity.startsWith(this.config.agentIdentityPrefix)
    ) {
      return true;
    }
    return participant.attributes?.[AGENT_STATE_ATTR] !== undefined;
  }

  /**
   * The primary agent: kind=AGENT and NOT publishing on behalf of someone else.
   * Owner of `lk.agent.state`; target of every RPC (set_mute, start_turn,
   * end_turn, cancel_turn, handle_game_event).
   */
  private isPrimaryAgent(participant: RemoteParticipant | Participant): boolean {
    if (!this.isAgentParticipant(participant)) return false;
    const onBehalf = participant.attributes?.[PUBLISH_ON_BEHALF_ATTR];
    return !onBehalf;
  }

  /**
   * An avatar worker bound to a specific primary's identity. Per LiveKit's
   * avatar plugin contract, workers carry `lk.publish_on_behalf=<primary>`.
   */
  private isAvatarWorkerFor(
    participant: RemoteParticipant | Participant,
    primaryIdentity: string | undefined
  ): boolean {
    if (!primaryIdentity) return false;
    if (participant.kind !== ParticipantKind.AGENT) return false;
    return participant.attributes?.[PUBLISH_ON_BEHALF_ATTR] === primaryIdentity;
  }

  private wrapVideoTrack(track: RemoteVideoTrack): DealerVideoTrack {
    let element: HTMLVideoElement | null = null;
    let detached = false;
    return {
      getElement: () => {
        // detach() is terminal — re-attaching here would resurrect a track the
        // SDK already considers gone (zombie element + leak). Fail loudly instead.
        if (detached) {
          throw new Error('DealerVideoTrack: getElement() called after detach()');
        }
        if (!element) {
          element = track.attach() as HTMLVideoElement;
        }
        return element;
      },
      detach: () => {
        detached = true;
        if (element) {
          track.detach(element);
          element.remove();
          element = null;
        }
      },
    };
  }

  private requireAgent(): RemoteParticipant {
    if (!this.agent) {
      throw new Error('No AI agent connected');
    }
    return this.agent;
  }

  private requireLocalParticipant(): Room['localParticipant'] {
    if (!this.room?.localParticipant) {
      throw new Error('DealerSDK room is not connected');
    }
    return this.room.localParticipant;
  }

  private emitError(code: string, message: string, cause?: unknown): void {
    this.emitTyped('error', { code, message, cause });
    this.logger.error(`DealerSDK error: ${code}`, { message, cause });
  }

  private emitTyped<K extends SDKEventName>(event: K, payload: SDKEventMap[K]): void {
    this.emit(event, payload);
  }
}
